import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Modal, Row } from 'antd';
import { SideWithAsset } from 'shared-types';

import PageCardEditor from 'components/Book/PageCardEditor';
import ResultOutput from 'components/Book/ResultOutput';

import useSessionStore from 'hooks/stores/useSessionStore';

import { enqueueSidePreviewGeneration, isCancelledSidePreviewError } from 'utils/sidePreviewQueue';
import {
  clearSidePreviewRequested,
  hasRequestedSidePreview,
  markSidePreviewRequested,
} from 'utils/sidePreviewRequests';

type Props = {
  open: boolean;
  onClose: () => void;
  sides: SideWithAsset[];
  sideIndex: number;
  onNavigate: (sideIndex: number) => void;
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  const tag = element?.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
};

// Bijna-fullscreen popup met alles wat in de book-layout rechts van de SidesList staat: de
// PageCardEditor (toolbar + grote preview) en de rechter sidebar (ResultOutput).
const SideEditorModal = ({ open, onClose, sides, sideIndex, onNavigate }: Props) => {
  const { t } = useTranslation();
  const isRightBarEnabled = useSessionStore(
    (state) => state.session?.options?.ui?.rightBar?.enabled !== false,
  );

  const sessionId = useSessionStore((state) => state.sessionId);

  const side = sides[sideIndex];
  const canGoPrev = sideIndex > 0;
  const canGoNext = sideIndex < sides.length - 1;

  // Doorbladeren naar een side zonder preview: de editor toont dan alleen een loader, want de
  // preview wordt on-demand gemaakt (options.onUpload.generatePreviewForSides). Vraag hem hier
  // aan, met voorrang in de wachtrij; het resultaat komt via de websocket in de store.
  const sideId = side?.id;
  const hasPreview = Boolean(side?.previewImageUrl && side.previewImageUrl !== '');
  useEffect(() => {
    if (!open || !sideId || hasPreview || hasRequestedSidePreview(sideId)) return;
    markSidePreviewRequested(sideId);
    enqueueSidePreviewGeneration(sessionId, sideId, { priority: 'high' }).catch((error) => {
      if (!isCancelledSidePreviewError(error)) {
        console.error('Side preview request failed', sideId, error);
      }
      clearSidePreviewRequested(sideId);
    });
  }, [open, sideId, hasPreview, sessionId]);

  // Pijltjestoetsen bladeren door de pagina's zolang de popup open is.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (isTypingTarget(event.target)) return;
      if (event.key === 'ArrowLeft' && canGoPrev) onNavigate(sideIndex - 1);
      if (event.key === 'ArrowRight' && canGoNext) onNavigate(sideIndex + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, canGoPrev, canGoNext, sideIndex, onNavigate]);

  if (!side) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width="min(1600px, 96vw)"
      style={{ top: 12, paddingBottom: 0 }}
      styles={{ body: { maxHeight: 'calc(100vh - 110px)', overflow: 'auto' } }}
      title={
        <Flex align="center" gap={12}>
          <Button
            icon={<LeftOutlined />}
            disabled={!canGoPrev}
            onClick={() => onNavigate(sideIndex - 1)}
            title={t('BookLayout.PreviousPage') as string}
          />
          <span>{t('BookLayout.PageOfTotal', { page: sideIndex + 1, total: sides.length })}</span>
          <Button
            icon={<RightOutlined />}
            disabled={!canGoNext}
            onClick={() => onNavigate(sideIndex + 1)}
            title={t('BookLayout.NextPage') as string}
          />
        </Flex>
      }
    >
      <Row gutter={[10, 10]}>
        <Col xs={{ span: 24 }} lg={{ span: isRightBarEnabled ? 18 : 24 }}>
          {/* key op side.id zodat de editor zijn interne state reset bij het bladeren */}
          <PageCardEditor key={side.id} side={side} onPdfPreviewGenerated={() => undefined} />
        </Col>
        {isRightBarEnabled && (
          <Col xs={{ span: 24 }} lg={{ span: 6 }}>
            <ResultOutput side={side} totalPageCount={sides.length} sideNumber={sideIndex + 1} />
          </Col>
        )}
      </Row>
    </Modal>
  );
};

export default SideEditorModal;
